import React, { useState } from "react";
import { Bid } from "../../api/types";
import defaultAvatar from "../../assets/images/default-avatar.jpg";
import PreviewReviewsModal from "../Rating/PreviewReviewsModal";
import { CompanyVerificationBadge } from "../CompanyVerificationBadge/CompanyVerificationBadge";
import ConfirmPopup from "../ConfirmPopUp/ConfirmPopUp";
import {
  formatToLocalDateTime,
  formatDateForInput,
} from "../../utils/dateFormat";

interface BidPreviewProps {
  bid: Bid;
  isOwner?: boolean;
  status?: number;
  winningCompany?: string;
  isWinning?: boolean;
  onSelectWinner?: (auctionId: string, company: string) => void;
  auctionId: string;
  moveDate?: string;
}

export const BidPreview: React.FC<BidPreviewProps> = ({
  bid,
  isOwner,
  isWinning,
  status,
  winningCompany,
  onSelectWinner,
  auctionId,
  moveDate,
}) => {
  const [reviewsCompanyId, setReviewsCompanyId] = useState<
    string | null | undefined
  >(null);
  const [showConfirmSelect, setShowConfirmSelect] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  if (!bid) return null;

  const isAnonymized = bid.company.startsWith("Company #");
  const hasAvatar = bid.avatar && bid.avatar.trim() !== "";
  const displayName = bid.companyName || bid.company;

  const handleSelectClick = () => {
    setShowConfirmSelect(true);
  };

  const handleConfirmSelect = async () => {
    if (!onSelectWinner) return;

    setIsSelecting(true);
    try {
      await onSelectWinner(auctionId, bid.id);
      setShowConfirmSelect(false);
    } catch (error) {
      // Error will be handled by parent
      console.error("Error selecting winner:", error);
    } finally {
      setIsSelecting(false);
    }
  };

  const isMoveDateValid = () => {
    if (!moveDate) return false;

    // Extract just the date part from ISO string (YYYY-MM-DD)
    const moveDateOnly = moveDate.split("T")[0];

    // Get today's date in local timezone using existing utility
    const todayStr = formatDateForInput(new Date());

    return moveDateOnly >= todayStr;
  };

  return (
    <div className="flex flex-col text-sm text-gray-800 min-w-0">
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <div className="relative flex-shrink-0 w-7 h-7">
            <img
              src={hasAvatar ? bid.avatar : defaultAvatar}
              alt={displayName}
              className={`w-full h-full rounded-full object-cover ${
                isAnonymized ? "blur-sm opacity-60" : ""
              }`}
            />
            {isAnonymized && (
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <span
              className={`block truncate font-medium ${
                isWinning ? "text-green-700" : ""
              } ${isAnonymized ? "blur-sm select-none" : ""}`}
              title={
                isAnonymized ? "Company name hidden for privacy" : displayName
              }
            >
              {displayName}
            </span>

            <div className="mt-0.5">
              <CompanyVerificationBadge
                status={bid.verificationStatus}
                size="sm"
              />
            </div>

            {isOwner &&
              typeof bid.ratingAvg === "number" &&
              typeof bid.ratingCount === "number" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReviewsCompanyId(bid.companyUserId);
                  }}
                  className="inline-flex items-center text-xs hover:underline mt-1"
                  title="View company reviews"
                >
                  <span aria-hidden className="text-yellow-600">
                    ★
                  </span>
                  <span className="ml-1 text-gray-600">
                    {bid.ratingCount === 0
                      ? "No reviews yet"
                      : `${bid.ratingAvg!.toFixed(1)} (${bid.ratingCount})`}
                  </span>
                </button>
              )}

            {isOwner && reviewsCompanyId && (
              <PreviewReviewsModal
                open={!!reviewsCompanyId}
                onClose={() => setReviewsCompanyId(null)}
                companyUserId={reviewsCompanyId}
              />
            )}
          </div>
        </div>

        {/* RIGHT: keep stable width so it doesn't crush layout */}
        <div className="flex items-start gap-2 shrink-0">
          <div className="text-right tabular-nums font-semibold text-gray-900">
            ${bid.amount}
          </div>

          {isOwner &&
            (status === 0 || status === 1 || status === 2 || status === 3) &&
            !winningCompany &&
            onSelectWinner &&
            isMoveDateValid() && (
              <button
                className="text-xs bg-green-500 text-white px-2 py-1 rounded shrink-0"
                onClick={handleSelectClick}
              >
                Select
              </button>
            )}

          {isWinning && (
            <span className="text-xs text-green-600 font-semibold shrink-0">
              Winner
            </span>
          )}
        </div>
      </div>

      <span
        className="ml-9 mt-2 text-xs text-gray-500 truncate"
        title={formatToLocalDateTime(bid.date)}
      >
        {formatToLocalDateTime(bid.date)}
      </span>

      <div className="ml-9">
        {bid.flatRate ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 mt-2 py-0.5 text-xs font-medium">
            Flat rate
          </span>
        ) : null}
      </div>

      <ConfirmPopup
        isOpen={showConfirmSelect}
        title="Select Winner"
        message={`Are you sure you want to select ${displayName} as the winner with a bid of $${bid.amount}?`}
        confirmText="Yes, Select Winner"
        cancelText="Cancel"
        onConfirm={handleConfirmSelect}
        onCancel={() => setShowConfirmSelect(false)}
        loading={isSelecting}
      />
    </div>
  );
};
